import * as React from "react";
import { createContext, useContext } from "react";
import { useLocationConfiguration } from "../../LocationConfiguration";
import { useAccount } from "../../Account";
import { useBranchLimits } from "../../BranchLimits";
import { useStationLocation } from "../../StationLocation";

type LocalHoldingsBranchContextType = { branchIdentifier: string | null };
export const LocalHoldingsBranchContext =
  createContext<LocalHoldingsBranchContextType>(null);

export const LocalHoldingsBranchProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { locationConfiguration } = useLocationConfiguration();
  const { visibleBranches } = useBranchLimits();
  const { patron } = useAccount();
  const { stationLocation } = useStationLocation();

  const branchIdentifier = React.useMemo(() => {
    if (stationLocation) {
      return stationLocation.identifier;
    }

    if (visibleBranches.length === 1) {
      return visibleBranches[0].identifier;
    }

    if (
      locationConfiguration?.pacSettings?.userActionSettings
        ?.useUsersBranchForAvailability
    ) {
      if (patron?.defaultRequestPickupBranch) {
        return patron.defaultRequestPickupBranch.toString();
      }
      if (patron?.issuingBranch) {
        return patron.issuingBranch.toString();
      }
    }

    return null;
  }, [visibleBranches, locationConfiguration, stationLocation, patron]);

  const contextValue: LocalHoldingsBranchContextType = {
    branchIdentifier,
  };

  return (
    <LocalHoldingsBranchContext.Provider value={contextValue}>
      {children}
    </LocalHoldingsBranchContext.Provider>
  );
};
export function useLocalHoldingsBranch() {
  const context = useContext(LocalHoldingsBranchContext);

  if (!context) {
    throw new Error(
      "useLocalHoldingsBranch must be used within a LocalHoldingsBranchProvider"
    );
  }

  return context;
}
